/**
 * PromiseSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides promise-specific
 * props (source type, linked invoice IDs for invoice attachment fetching).
 *
 * Fetches linked transaction IDs internally via GraphQL rather than
 * relying on page-level data (which may not include invoice details).
 *
 * Follows the exact same pattern as TransactionSendCopyInvoiceModal.
 */

import { useCallback, useMemo } from "react";
import {
  SingleEmailModal,
  type EmailAttachment,
} from "@/components/shared/single-email-modal";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { usePromiseTransactionsPaginatedGraphQL } from "@/hooks/usePromiseTransactionsGraphQL";
import { useLogPromiseEmail } from "@/hooks/useLogPromiseEmail";
import { toast } from "sonner";
import { trackError } from "@/lib/telemetry";

export interface PromiseSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promiseId: string;
  customerId: string;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
}

export function PromiseSendEmailModal({
  open,
  onOpenChange,
  promiseId,
  customerId,
  customerName = "",
  accountNumber = "",
  onSuccess,
}: Readonly<PromiseSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const logPromiseEmail = useLogPromiseEmail();

  const { transactions: promiseTransactions } =
    usePromiseTransactionsPaginatedGraphQL(open ? promiseId : undefined, {
      pageSize: 500,
    });

  const linkedTransactionIds = useMemo(
    () => promiseTransactions.map((t) => t.id).filter((id) => id > 0),
    [promiseTransactions],
  );

  const handleFetchAttachments = useCallback(
    async (
      onProgress: (done: number, total: number) => void,
    ): Promise<EmailAttachment[]> => {
      if (linkedTransactionIds.length === 0) return [];

      onProgress(0, linkedTransactionIds.length);

      const results = await Promise.allSettled(
        linkedTransactionIds.map(async (txId, idx) => {
          const doc = await downloadDocumentAsBase64(txId);
          onProgress(idx + 1, linkedTransactionIds.length);
          return doc;
        }),
      );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<EmailAttachment> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [linkedTransactionIds, downloadDocumentAsBase64],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logPromiseEmail.mutate(
        {
          sourceId: promiseId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error("Failed to save promise email to history"));
            toast.error("Email was sent but failed to save to history. Please contact support if the issue persists.");
          },
        },
      );
    },
    [logPromiseEmail, promiseId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="promise"
      sourceId={Number(promiseId)}
      transactionIds={linkedTransactionIds}
      callbackEventName="creditcontrol:promise-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={["promises", "general"]}
      allowedTemplateTypes={["email"]}
    />
  );
}
